import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { XIcon, SaveIcon, CalendarIcon, DollarIcon, MessageIcon, PlusIcon, EditIcon, CopyIcon, EmailIcon, PhoneIcon } from '../components/Icons'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import { PDFDownloadLink } from '@react-pdf/renderer'
import DealPDF from '../components/DealPDF'
import UserAvatarStack from '../components/UserAvatarStack'
import { FullWidthContent } from '../components/Layout'
import SMSClient from '../components/SMSClient'
import DealValidationModal from '../components/DealValidationModal'

// Re-use existing interfaces from DealEditModal
interface Person {
  id: number
  name: string
  email?: string
  phone?: string
  is_primary: boolean
}

interface DealCustomer {
  id: number
  customer_id: number
  name: string
  email?: string
  phone?: string
  role: 'applicant' | 'co_applicant'
  order_position: number
  referred_by?: string
  partner_id?: number | null
}

interface Deal {
  id: number
  name: string
  title?: string // Legacy field, now using 'name'
  amount: string
  customer_name: string
  customer_phone?: string
  customer_email?: string
  customer_id: number
  deal_type_id?: string
  type_name?: string
  deal_version?: 'commercial' | 'residential'
  lender_id?: number | null
  lender_name?: string | null
  commission?: {
    totalPoints: number
    totalPercentage: number
    grossCommission: number
    agencyFee: number
    closingFeeAmount: number
    netCommission: number
  }
  base_points: string
  bonus_points: string
  volume_points: string
  closing_date?: string
  expected_close_date?: string
  status_id: string
  status_name: string
  status_color: string
  status_is_archived?: boolean
  board_id?: number | null
  board_name?: string | null
  board_type?: 'deal' | 'lead' | null
  paired_board_id?: number | null
  position: number
  description?: string
  created_at: string
  updated_at: string
  deal_customers?: DealCustomer[] // Applicant + co-applicants
}

interface DealHistory {
  id: number
  action_type: string
  previous_values?: string
  new_values?: string
  comment?: string
  comment_type?: string
  field_changed?: string
  created_at: string
  created_by_username?: string
  first_name?: string
  last_name?: string
  status_name_at_time?: string
  status_color_at_time?: string
  is_pinned?: boolean
}

interface Commission {
  totalPoints: number
  totalPercentage: number
  grossCommission: number
  agencyFee: number
  closingFeeAmount: number
  netCommission: number
}

interface Partner {
  id: number
  name: string
  first_name: string
  last_name: string
  email?: string
  phone?: string
  agency?: string
  commission_cut?: number
}

interface CommissionSplit {
  id: number
  deal_id: number
  partner_id: number
  split_type: 'percentage' | 'fixed_amount'
  split_value: number
  calculated_amount?: number
  notes?: string
  partner_name?: string
  partner_first_name?: string
  partner_last_name?: string
  partner_email?: string
  partner_phone?: string
  partner_agency?: string
}

interface DealType {
  id: number
  name: string
}

interface Customer {
  id: number
  name: string
  email?: string
  phone?: string
}

interface Comment {
  id: number
  content: string
  created_at: string
  created_by_username: string
}

interface WorkflowStep {
  id: number
  workflow_template_id: number
  name: string
  description?: string
  color: string
  position: number
  is_start_step: boolean
  is_end_step: boolean
  metadata?: any
}

interface WorkflowAutomation {
  id: number
  workflow_step_id: number
  trigger_event: string
  action_type: string
  action_config: any
  delay_hours: number
  is_active: boolean
}

interface AutomationExecution {
  id: number
  deal_id: number
  workflow_automation_id: number
  triggered_at: string
  trigger_source: string
  status: string
  action_type: string
  trigger_event: string
  step_name: string
  step_color: string
}

interface WorkflowInstance {
  id: number
  deal_id: number
  workflow_template_id: number
  current_step_id?: number
  step_entered_at: string
  progress_history: WorkflowHistoryEntry[]
  template_name: string
  template_description?: string
  deal_type_name: string
  current_step?: WorkflowStep
  all_steps: WorkflowStep[]
  automation_executions: AutomationExecution[]
  current_step_automations: WorkflowAutomation[]
}

interface WorkflowHistoryEntry {
  step_id: number
  step_name: string
  entered_at: string
  action: string
  previous_step_id?: number
  previous_step_name?: string
  moved_by?: number
  comment?: string
}

interface DealAssignment {
  id: number
  deal_id: number
  user_id: number
  assignment_type: 'primary' | 'collaborator'
  notes?: string
  assigned_at: string
  assigned_by: number
  first_name: string
  last_name: string
  email: string
  profile_picture_url?: string
  assigned_by_first_name?: string
  assigned_by_last_name?: string
}

interface TeamMember {
  id: number
  user_id: number
  first_name: string
  last_name: string
  email: string
  role: string
  profile_picture_url?: string
}

interface Task {
  id: number
  customer_id: number | null
  deal_id?: number
  lead_id?: number
  description: string
  due_date: string
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  customer_name?: string
  completed_at?: string
}

interface CompletionModal {
  isOpen: boolean
  task: Task | null
}

const STATUS_SHAPE = {
  INDENT_PX: 12,
  TIP_PX: 4,
}

const buildStatusClipPath = (hasLeftIndent: boolean, hasRightArrow: boolean): string => {
  const indent = STATUS_SHAPE.INDENT_PX
  const tip = STATUS_SHAPE.TIP_PX

  if (hasLeftIndent && hasRightArrow) {
    return `polygon(${indent}px 0, calc(100% - ${indent}px) 0, calc(100% - ${tip}px) 50%, calc(100% - ${indent}px) 100%, ${indent}px 100%, ${tip}px 50%)`
  }

  if (hasLeftIndent && !hasRightArrow) {
    return `polygon(${indent}px 0, 100% 0, 100% 100%, ${indent}px 100%, ${tip}px 50%)`
  }

  if (!hasLeftIndent && hasRightArrow) {
    return `polygon(0 0, calc(100% - ${indent}px) 0, calc(100% - ${tip}px) 50%, calc(100% - ${indent}px) 100%, 0 100%)`
  }

  return 'none'
}

const DealDetail: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  // Deal data state
  const [deal, setDeal] = useState<Deal | null>(null)
  const [originalDeal, setOriginalDeal] = useState<Deal | null>(null)
  const [history, setHistory] = useState<DealHistory[]>([])
  const [commission, setCommission] = useState<Commission | null>(null)
  const [commissionSplits, setCommissionSplits] = useState<CommissionSplit[]>([])
  const [partners, setPartners] = useState<Partner[]>([])
  const [dealTypes, setDealTypes] = useState<DealType[]>([])
  const [dealStatuses, setDealStatuses] = useState<any[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [dealCustomers, setDealCustomers] = useState<DealCustomer[]>([])
  
  // Assignment state
  const [assignments, setAssignments] = useState<DealAssignment[]>([])
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const [assignmentLoading, setAssignmentLoading] = useState(false)
  
  // Workflow state
  const [workflowInstance, setWorkflowInstance] = useState<WorkflowInstance | null>(null)
  const [workflowLoading, setWorkflowLoading] = useState(false)
  const [showWorkflowMove, setShowWorkflowMove] = useState(false)
  const [selectedTargetStep, setSelectedTargetStep] = useState<WorkflowStep | null>(null)
  const [workflowComment, setWorkflowComment] = useState('')
  
  // UI state
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false)
  const [selectedNewStatus, setSelectedNewStatus] = useState<any>(null)
  // Deal status rules — missing required fields modal
  const [showDealValidationModal, setShowDealValidationModal] = useState(false)
  const [dealValidationState, setDealValidationState] = useState<{
    missingFields: string[]
    targetStatusId: number
    targetStatusName: string
  } | null>(null)
  const [showToolsModal, setShowToolsModal] = useState(false)
  const [copiedItem, setCopiedItem] = useState<string | null>(null)
  
  // Notes functionality state
  const [newComment, setNewComment] = useState('')
  const [commentType, setCommentType] = useState('comment')
  const [addingComment, setAddingComment] = useState(false)
  
  // Comment editing state
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null)
  const [editCommentText, setEditCommentText] = useState('')
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  
  // Tab state for multi-function section
  const [activeTab, setActiveTab] = useState<'history' | 'sms' | 'email' | 'compliance'>('history')
  
  
  // Financial settings for commission calculation
  const [financialSettings, setFinancialSettings] = useState<{
    agencyFeePercentage: number
    closingFeeAmount: number
  }>({ agencyFeePercentage: 0, closingFeeAmount: 0 })

  // Tasks state
  const [dealTasks, setDealTasks] = useState<any[]>([])
  const [tasksLoading, setTasksLoading] = useState(false)

  // Task completion modal state
  const [completionModal, setCompletionModal] = useState<CompletionModal>({ isOpen: false, task: null })
  const [followUpDescription, setFollowUpDescription] = useState('')
  const [followUpPriority, setFollowUpPriority] = useState<'low' | 'medium' | 'high'>('medium')
  
  // Task creation modal state
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false)
  const [followUpSelectedDate, setFollowUpSelectedDate] = useState('')
  const [followUpCustomDate, setFollowUpCustomDate] = useState('')

  // PDF export state
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [smsMessages, setSmsMessages] = useState<any[]>([])
  const [customerPersons, setCustomerPersons] = useState<Person[]>([])
  const [pdfLanguage, setPdfLanguage] = useState<'en' | 'fr'>('en')

  // Lender search state
  const [lenderSearchQuery, setLenderSearchQuery] = useState('')
  const [lenderSearchResults, setLenderSearchResults] = useState<any[]>([])
  const [showLenderDropdown, setShowLenderDropdown] = useState(false)
  const [isSearchingLenders, setIsSearchingLenders] = useState(false)

  // Partner search state for commission split inline editing
  const [partnerSearchQuery, setPartnerSearchQuery] = useState('')
  const [partnerSearchResults, setPartnerSearchResults] = useState<Partner[]>([])
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false)
  const [showPartnerCreationModal, setShowPartnerCreationModal] = useState(false)
  
  // Inline editing state for commission splits
  const [editingNewSplit, setEditingNewSplit] = useState(false)
  const [newSplitData, setNewSplitData] = useState<{
    partner: Partner | null
    split_type: 'percentage' | 'fixed_amount'
    split_value: string
    notes: string
  }>({
    partner: null,
    split_type: 'percentage',
    split_value: '',
    notes: ''
  })

  // Helper function to format date as local YYYY-MM-DD string
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Helper function to get next 7 days for date selection
  const getNext7Days = () => {
    const days = []
    const today = new Date()
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + i)
      
      let label = ''
      if (i === 0) label = t('dashboard.periods.today')
      else if (i === 1) label = t('tasks.tomorrow')
      else label = date.toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      
      days.push({
        date: formatLocalDate(date),
        label,
        isToday: i === 0,
        isTomorrow: i === 1
      })
    }
    
    return days
  }

  // Format phone number to 10 digits (remove +1, hyphens, dots, spaces, parentheses)
  const formatPhoneNumber = (phone: string | undefined): string | null => {
    if (!phone) return null
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '')
    // If it starts with 1 and has 11 digits, remove the 1
    if (digits.length === 11 && digits.startsWith('1')) {
      return digits.substring(1)
    }
    // Return only if it's exactly 10 digits
    if (digits.length === 10) {
      return digits
    }
    // Return original if it doesn't match expected format
    return phone
  }

  // Copy text to clipboard with feedback
  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedItem(type)
      setTimeout(() => setCopiedItem(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // Load deal data on component mount
  useEffect(() => {
    if (id) {
      fetchDealData()
      fetchDealTypes()
      fetchAssignments()
      fetchTeamMembers()
      fetchDealTasks()
    }
  }, [id])

  // Re-fetch statuses scoped to the deal's board once board_id is known
  useEffect(() => {
    if (deal?.board_id !== undefined) {
      fetchDealStatuses(deal.board_id)
    }
  }, [deal?.board_id])

  // Load workflow data after deal data is loaded
  useEffect(() => {
    if (id && deal) {
      fetchWorkflowData()
    }
  }, [id, deal])

  // Initialize PDF language from i18n and fetch current user
  useEffect(() => {
    setPdfLanguage(i18n.language as 'en' | 'fr')
    fetchCurrentUser()
  }, [])

  // Fetch SMS messages and customer persons when deal is loaded
  useEffect(() => {
    if (deal) {
      fetchSmsMessages()
      // Note: Removed customer_persons - now using deal_customers only
    }
  }, [deal])

  // ─── Commission debug logger ───────────────────────────────────────────────
  // Logs the full net commission calculation to the browser console whenever
  // any commission-affecting value changes. Remove this useEffect when done.
  useEffect(() => {
    if (!deal || !commission) return

    const loanAmount   = parseFloat(String(deal.amount || 0))
    const basePoints   = Number(deal.base_points   || 0)
    const bonusPoints  = Number(deal.bonus_points  || 0)
    const volumePoints = Number(deal.volume_points || 0)
    const totalPoints  = basePoints + bonusPoints + volumePoints
    const totalPct     = totalPoints / 10000              // e.g. 100 pts → 0.01 → 1 %

    const grossCommission = loanAmount * totalPct
    const agencyFee       = grossCommission * (financialSettings.agencyFeePercentage / 100)
    const closingFee      = financialSettings.closingFeeAmount
    const netBeforeSplits = Math.max(0, grossCommission - agencyFee - closingFee)

    const splitsDetail = commissionSplits.map((split, i) => {
      const dollarAmount =
        split.split_type === 'percentage'
          ? netBeforeSplits * (parseFloat(String(split.split_value)) / 100)
          : parseFloat(String(split.split_value))
      return {
        [`Split ${i + 1} – ${split.partner_name || 'Partner'}`]: {
          type:           split.split_type,
          value:          split.split_type === 'percentage' ? `${split.split_value}%` : `$${split.split_value}`,
          dollar_impact:  `- $${dollarAmount.toFixed(2)}`,
        }
      }
    })

    const totalSplitDeduction = commissionSplits.reduce((sum, split) => {
      return sum + (
        split.split_type === 'percentage'
          ? netBeforeSplits * (parseFloat(String(split.split_value)) / 100)
          : parseFloat(String(split.split_value))
      )
    }, 0)

    const finalNet = Math.max(0, netBeforeSplits - totalSplitDeduction)

    console.group(`💰 Net Commission Calculation — Deal: "${deal.name || deal.id}"`)

    console.group('① Inputs')
    console.table({
      'Loan Amount': `$${loanAmount.toFixed(2)}`,
      'Base Points':   basePoints,
      'Bonus Points':  bonusPoints,
      'Volume Points': volumePoints,
      'Total Points':  totalPoints,
      'Total %':       `${(totalPct * 100).toFixed(4)}%`,
      'Agency Fee %':  `${financialSettings.agencyFeePercentage}%`,
      'Closing Fee':   `$${closingFee.toFixed(2)}`,
    })
    console.groupEnd()

    console.group('② Gross → Net (before splits)')
    console.table({
      'Gross Commission  (loan × total%)': `$${grossCommission.toFixed(2)}`,
      '– Agency Fee      (gross × fee%)':  `- $${agencyFee.toFixed(2)}`,
      '– Closing Fee':                      `- $${closingFee.toFixed(2)}`,
      '= Net (before splits)':              `$${netBeforeSplits.toFixed(2)}`,
    })
    console.groupEnd()

    if (commissionSplits.length > 0) {
      console.group(`③ Commission Splits (${commissionSplits.length})`)
      splitsDetail.forEach(s => console.table(s))
      console.log(`Total split deduction: - $${totalSplitDeduction.toFixed(2)}`)
      console.groupEnd()
    } else {
      console.log('③ No commission splits')
    }

    console.group('④ Final Net Commission')
    console.log(`Frontend calculated:  $${finalNet.toFixed(2)}`)
    console.log(`commission.netCommission (from API, pre-splits): $${commission.netCommission.toFixed(2)}`)
    console.log(`Displayed in header:  $${Math.max(0, commission.netCommission - totalSplitDeduction).toFixed(2)}`)
    console.groupEnd()

    console.groupEnd()
  }, [deal, commission, commissionSplits, financialSettings])

  const fetchDealData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/deals/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const dealData = await response.json()
        setDeal(dealData)
        setOriginalDeal(dealData) // Keep original for cancel functionality
        setHistory(dealData.history || [])
        setCommission(dealData.commission)
        setCommissionSplits(dealData.commission_splits || [])
        setDealCustomers(dealData.deal_customers || [])
        
        // Fetch additional deal data
        await fetchFinancialSettings()
        await fetchPartners()
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to load deal')
      }
    } catch (error) {
      console.error('Error fetching deal:', error)
      setError('Failed to load deal')
    } finally {
      setLoading(false)
    }
  }

  // Lightweight history-only refresh — used after comment/pin/workflow changes.
  // Does not set loading state or re-fetch the rest of the deal.
  const fetchHistory = async () => {
    if (!id) return
    try {
      const response = await makeApiCall(`api/deals/${id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      if (response.ok) {
        const data = await response.json()
        setHistory(data.history || [])
      }
    } catch (error) {
      console.error('Error refreshing history:', error)
    }
  }

  const fetchDealTypes = async () => {
    try {
      const token = localStorage.getItem('token')
      
      // Try user-specific deal types first
      let response = await makeApiCall('api/settings/deal-types', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.deal_types && data.deal_types.length > 0) {
          setDealTypes(data.deal_types)
          return
        }
      }

      // Fallback to global deal types if user-specific ones don't exist
      response = await makeApiCall('api/deals/types', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDealTypes(data.deal_types || [])
      }
    } catch (error) {
      console.error('Error fetching deal types:', error)
    }
  }

  const fetchDealStatuses = async (boardId?: number | null) => {
    try {
      const token = localStorage.getItem('token')
      const url = boardId
        ? `api/deals/statuses?board_id=${boardId}`
        : 'api/deals/statuses'
      const response = await makeApiCall(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        const statusesArray = Array.isArray(data) ? data : (data.statuses || [])
        setDealStatuses(statusesArray)
      } else {
        setDealStatuses([])
      }
    } catch (error) {
      console.error('Error fetching deal statuses:', error)
      setDealStatuses([])
    }
  }

  const fetchDealCustomers = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/deals/${id}/customers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setDealCustomers(data.customers || [])
      }
    } catch (error) {
      console.error('Error fetching deal customers:', error)
    }
  }

  // Helper function to get unique partner IDs from deal customers
  const getUniquePartnerIds = (): number[] => {
    if (!dealCustomers || dealCustomers.length === 0) return []
    
    const partnerIds = new Set<number>()
    
    dealCustomers.forEach(customer => {
      if (customer.partner_id) {
        partnerIds.add(customer.partner_id)
      }
    })
    
    return Array.from(partnerIds)
  }

  // Fetch partner names for display
  const [partnerNames, setPartnerNames] = useState<string[]>([])

  useEffect(() => {
    const fetchPartnerNames = async () => {
      const partnerIds = getUniquePartnerIds()
      if (partnerIds.length === 0) {
        setPartnerNames([])
        return
      }

      try {
        const names: string[] = []
        for (const partnerId of partnerIds) {
          const response = await makeApiCall(`api/partners/${partnerId}`)
          if (response.ok) {
            const data = await response.json()
            names.push(data.partner.name)
          }
        }
        setPartnerNames(names)
      } catch (error) {
        console.error('Error fetching partner names:', error)
      }
    }

    fetchPartnerNames()
  }, [dealCustomers])

  const fetchFinancialSettings = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall('api/settings/financial', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        // Parse the settings array to extract values
        const agencyFeeSetting = data.settings?.find((s: any) => s.setting_key === 'agency_fee_percentage')
        const closingFeeSetting = data.settings?.find((s: any) => s.setting_key === 'closing_fee_amount')
        
        setFinancialSettings({
          agencyFeePercentage: parseFloat(agencyFeeSetting?.setting_value || '0') || 0,
          closingFeeAmount: parseFloat(closingFeeSetting?.setting_value || '0') || 0
        })
      }
    } catch (error) {
      console.error('Error fetching financial settings:', error)
    }
  }

  const fetchPartners = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall('api/partners', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setPartners(data.partners || [])
      }
    } catch (error) {
      console.error('Error fetching partners:', error)
    }
  }

  // Partner search function for commission split modal
  const searchPartners = (query: string) => {
    if (!query.trim()) {
      setPartnerSearchResults([])
      return
    }

    const filtered = partners.filter(partner => {
      const fullName = `${partner.first_name} ${partner.last_name}`.toLowerCase()
      const name = partner.name?.toLowerCase() || ''
      const email = partner.email?.toLowerCase() || ''
      const searchQuery = query.toLowerCase()
      
      return fullName.includes(searchQuery) || name.includes(searchQuery) || email.includes(searchQuery)
    })
    
    setPartnerSearchResults(filtered)
  }

  // Create new partner
  const createPartner = async (firstName: string, lastName: string, email?: string, phone?: string, agency?: string, commissionCut?: string) => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeJsonApiCall('api/partners', 'POST', {
        first_name: firstName,
        last_name: lastName,
        email: email || undefined,
        phone: phone || undefined,
        agency: agency || undefined,
        commission_cut: commissionCut || undefined
      })

      if (response.ok) {
        const data = await response.json()
        // Add to partners list
        setPartners([...partners, data.partner])
        return data.partner
      } else if (response.status === 409) {
        // Partner already exists
        const data = await response.json()
        return data.partner
      }
      return null
    } catch (error) {
      console.error('Error creating partner:', error)
      return null
    }
  }


  const handleSaveNewSplit = async () => {
    if (!newSplitData.partner || !newSplitData.split_value) {
      alert(t('deals.dealDetail.selectPartner'))
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await makeJsonApiCall('api/commission-splits', 'POST', {
        deal_id: id,
        partner_id: newSplitData.partner.id,
        split_type: newSplitData.split_type,
        split_value: parseFloat(newSplitData.split_value),
        notes: newSplitData.notes || undefined
      })

      if (response.ok) {
        const data = await response.json()
        setCommissionSplits([...commissionSplits, data.split])
        setEditingNewSplit(false)
        setNewSplitData({
          partner: null,
          split_type: 'percentage',
          split_value: '',
          notes: ''
        })
        setPartnerSearchQuery('')
      } else {
        const errorData = await response.json()
        alert(errorData.message || t('deals.dealDetail.splitCreationError'))
      }
    } catch (error) {
      console.error('Error creating commission split:', error)
      alert(t('deals.dealDetail.splitCreationError'))
    }
  }

  const handleCancelNewSplit = () => {
    setEditingNewSplit(false)
    setNewSplitData({
      partner: null,
      split_type: 'percentage',
      split_value: '',
      notes: ''
    })
    setPartnerSearchQuery('')
    setShowPartnerDropdown(false)
  }

  const handleUpdateSplit = async (splitId: number, updates: {
    split_type?: 'percentage' | 'fixed_amount'
    split_value?: number
    notes?: string
  }) => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeJsonApiCall(`api/commission-splits/${splitId}`, 'PUT', updates)

      if (response.ok) {
        const data = await response.json()
        setCommissionSplits(commissionSplits.map(s => s.id === splitId ? data.split : s))
      } else {
        const errorData = await response.json()
        alert(errorData.message || t('deals.dealDetail.splitUpdateError'))
      }
    } catch (error) {
      console.error('Error updating commission split:', error)
      alert(t('deals.dealDetail.splitUpdateError'))
    }
  }

  const handleDeleteSplit = async (splitId: number) => {
    if (!confirm(t('deals.dealDetail.confirmDeleteSplit'))) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/commission-splits/${splitId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        setCommissionSplits(commissionSplits.filter(s => s.id !== splitId))
      } else {
        const errorData = await response.json()
        alert(errorData.message || t('deals.dealDetail.splitDeleteError'))
      }
    } catch (error) {
      console.error('Error deleting commission split:', error)
      alert(t('deals.dealDetail.splitDeleteError'))
    }
  }

  const fetchAssignments = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/deals/${id}/assignments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setAssignments(data.assignments || [])
      }
    } catch (error) {
      console.error('Error fetching deal assignments:', error)
    }
  }

  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall('api/collaborators', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setTeamMembers(data.collaborators || [])
      }
    } catch (error) {
      console.error('Error fetching team members:', error)
    }
  }

  const fetchDealTasks = async () => {
    try {
      setTasksLoading(true)
      const token = localStorage.getItem('token')
      console.log('Fetching tasks for deal ID:', id)
      const response = await makeApiCall(`api/tasks?deal_id=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        console.log('Tasks response:', data)
        console.log('Tasks array:', data.tasks)
        // Filter tasks to only include those for this specific deal
        const filteredTasks = (data.tasks || []).filter((task: any) => 
          task.deal_id && task.deal_id.toString() === id?.toString()
        )
        console.log('Filtered tasks for deal', id, ':', filteredTasks)
        setDealTasks(filteredTasks)
      }
    } catch (error) {
      console.error('Error fetching deal tasks:', error)
    } finally {
      setTasksLoading(false)
    }
  }

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token')
      if (token) {
        // Decode JWT token to get user info
        const payload = JSON.parse(atob(token.split('.')[1]))
        
        // Set basic user info from JWT token as fallback
        const basicUser = {
          id: payload.id,
          first_name: payload.firstName || '',
          last_name: payload.lastName || '',
          email: payload.email || '',
          phone: ''
        }
        
        try {
          // Try to fetch full user details from API
          const response = await makeApiCall(`api/users/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })

          if (response.ok) {
            const data = await response.json()
            const userData = data.user
            setCurrentUser({
              id: userData.id,
              first_name: userData.first_name,
              last_name: userData.last_name,
              email: userData.email,
              phone: userData.phone || ''
            })
          } else {
            // Use JWT data as fallback if API call fails
            setCurrentUser(basicUser)
          }
        } catch (apiError) {
          // Use JWT data as fallback if API call fails
          console.log('Using JWT data for current user')
          setCurrentUser(basicUser)
        }
      }
    } catch (error) {
      console.error('Error fetching current user:', error)
    }
  }

  const fetchSmsMessages = async () => {
    // Get the first deal customer's ID to fetch SMS messages
    if (!deal?.deal_customers || deal.deal_customers.length === 0) return
    
    const firstCustomerId = deal.deal_customers[0].customer_id
    if (!firstCustomerId) return
    
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/sms/conversations/customer/${firstCustomerId}/messages?limit=500`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setSmsMessages(data.messages || [])
      }
    } catch (error) {
      console.error('Error fetching SMS messages:', error)
    }
  }

  const fetchWorkflowData = async () => {
    console.log('🔍 fetchWorkflowData called, id:', id)
    try {
      setWorkflowLoading(true)
      const token = localStorage.getItem('token')
      console.log('Fetching workflow data for deal:', id)
      console.log('Deal type_id:', deal?.deal_type_id, 'Deal version:', deal?.deal_version)
      const response = await makeApiCall(`api/deals/${id}/workflow`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      console.log('Workflow response status:', response.status)
      if (response.ok) {
        const data = await response.json()
        console.log('Workflow response data:', data)
        setWorkflowInstance(data.workflow_instance)
      } else {
        console.error('Workflow response not ok:', response.status, response.statusText)
        const errorText = await response.text()
        console.error('Workflow error text:', errorText)
      }
    } catch (error) {
      console.error('Error fetching workflow data:', error)
    } finally {
      setWorkflowLoading(false)
    }
  }

  // Lender search function
  const searchLenders = async (query: string) => {
    if (!query.trim()) {
      setLenderSearchResults([])
      return
    }

    try {
      setIsSearchingLenders(true)
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/lenders/search?q=${encodeURIComponent(query)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setLenderSearchResults(data.lenders || [])
      } else {
        setLenderSearchResults([])
      }
    } catch (error) {
      console.error('Error searching lenders:', error)
      setLenderSearchResults([])
    } finally {
      setIsSearchingLenders(false)
    }
  }

  // Create new lender
  const createLender = async (name: string) => {
    if (!name.trim()) return null

    try {
      const token = localStorage.getItem('token')
      const response = await makeJsonApiCall('api/lenders', 'POST', {
        name: name.trim()
      })

      if (response.ok) {
        const data = await response.json()
        return data.lender
      } else if (response.status === 409) {
        // Lender already exists
        const data = await response.json()
        return data.lender
      }
      return null
    } catch (error) {
      console.error('Error creating lender:', error)
      return null
    }
  }

  // Handle lender selection
  const handleSelectLender = async (lender: any) => {
    if (!deal) return

    try {
      // Update deal field (this will refresh the deal data)
      await updateDealField('lender_id', lender.id)
      
      // Update local state immediately for better UX
      setDeal({ ...deal, lender_id: lender.id, lender_name: lender.name })
      
      // Close dropdown and clear search
      setShowLenderDropdown(false)
      setLenderSearchQuery('')
    } catch (error) {
      console.error('Error selecting lender:', error)
    }
  }

  // Handle creating new lender
  const handleCreateLender = async () => {
    if (!deal || !lenderSearchQuery.trim()) return

    try {
      const newLender = await createLender(lenderSearchQuery)
      if (newLender) {
        // Update deal field (this will refresh the deal data)
        await updateDealField('lender_id', newLender.id)
        
        // Update local state immediately for better UX
        setDeal({ ...deal, lender_id: newLender.id, lender_name: newLender.name })
        
        // Close dropdown and clear search
        setShowLenderDropdown(false)
        setLenderSearchQuery('')
      }
    } catch (error) {
      console.error('Error creating and selecting lender:', error)
    }
  }

  const handleInitializeWorkflow = async () => {
    if (!id) return

    try {
      setWorkflowLoading(true)
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/deals/${id}/workflow/initialize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        // Refresh workflow data
        await fetchWorkflowData()
        alert('Workflow initialized successfully!')
      } else {
        const errorData = await response.json()
        alert('Error: ' + (errorData.error || 'Failed to initialize workflow'))
      }
    } catch (error) {
      console.error('Error initializing workflow:', error)
      alert('Error: Failed to initialize workflow')
    } finally {
      setWorkflowLoading(false)
    }
  }

  const handleWorkflowMove = async () => {
    if (!selectedTargetStep || !id) return

    try {
      setWorkflowLoading(true)
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/deals/${id}/workflow/move`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          target_step_id: selectedTargetStep.id,
          comment: workflowComment
        })
      })

      if (response.ok) {
        await fetchWorkflowData()
        await fetchHistory()
        setShowWorkflowMove(false)
        setSelectedTargetStep(null)
        setWorkflowComment('')
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to move workflow step')
      }
    } catch (error) {
      console.error('Error moving workflow step:', error)
      setError('Failed to move workflow step')
    } finally {
      setWorkflowLoading(false)
    }
  }

  const updateDealField = async (fieldName: string, value: any) => {
    if (!deal) return

    try {
      const response = await makeApiCall(`api/deals/${deal.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ [fieldName]: value })
      })

      if (response.ok) {
        // Use the PUT response directly — no full page re-fetch needed.
        // The backend returns the updated deal with recalculated commission.
        const updated = await response.json()
        setDeal(prev => prev ? { ...prev, ...updated } : updated)
        setOriginalDeal(prev => prev ? { ...prev, ...updated } : updated)
        if (updated.commission) setCommission(updated.commission)
      } else {
        const errorData = await response.json()
        setError(errorData.error || 'Failed to update deal')
      }
    } catch (error) {
      console.error('Error updating deal:', error)
      setError('Failed to update deal')
    }
  }

  // Perform a status change directly — no confirmation modal.
  // Shows the DealValidationModal if required fields are missing.
  const performStatusChange = async (newStatus: { id: number; name: string; color?: string }) => {
    if (!newStatus || !deal) return

    try {
      setSaving(true)

      const response = await makeApiCall(`api/deals/${deal.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ status_id: newStatus.id })
      })

      if (response.ok) {
        const updated = await response.json()
        setDeal(prev => prev ? { ...prev, ...updated } : updated)
        setOriginalDeal(prev => prev ? { ...prev, ...updated } : updated)
        if (updated.commission) setCommission(updated.commission)
        setSelectedNewStatus(null)
        return
      }

      const errorData = await response.json()
      if (errorData.requiresValidation && errorData.missingFields?.length) {
        setDealValidationState({
          missingFields: errorData.missingFields,
          targetStatusId: newStatus.id,
          targetStatusName: newStatus.name
        })
        setShowDealValidationModal(true)
      } else {
        setError(errorData.error || 'Failed to change status')
      }
    } catch (error) {
      console.error('Error changing status:', error)
      setError('Failed to change status')
    } finally {
      setSaving(false)
    }
  }

  // Keep for any remaining callers (e.g. old modal button)
  const handleStatusChange = () => {
    if (selectedNewStatus) performStatusChange(selectedNewStatus)
  }

  const handleAddComment = async () => {
    if (!newComment.trim() || !deal) return

    try {
      setAddingComment(true)
      const response = await makeJsonApiCall(`api/deals/${deal.id}/comments`, 'POST', {
        comment: newComment,
        comment_type: commentType
      })

      if (response.ok) {
        setNewComment('')
        setCommentType('comment')
        await fetchHistory()
      } else {
        console.error('Failed to add comment:', await response.text())
        setError('Failed to add comment')
      }
    } catch (error) {
      console.error('Error adding comment:', error)
      setError('Failed to add comment')
    } finally {
      setAddingComment(false)
    }
  }

  const handleEditComment = async (commentId: number) => {
    if (!editCommentText.trim() || !deal) return

    try {
      const response = await makeJsonApiCall(`api/deals/${deal.id}/comments/${commentId}`, 'PUT', {
        comment: editCommentText
      })

      if (response.ok) {
        setEditingCommentId(null)
        setEditCommentText('')
        await fetchHistory()
      } else {
        console.error('Failed to edit comment:', await response.text())
        setError('Failed to edit comment')
      }
    } catch (error) {
      console.error('Error editing comment:', error)
      setError('Failed to edit comment')
    }
  }

  const handleDeleteComment = async () => {
    if (!deletingCommentId || !deal) return

    try {
      const response = await makeApiCall(`api/deals/${deal.id}/comments/${deletingCommentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.ok) {
        setShowDeleteModal(false)
        setDeletingCommentId(null)
        await fetchHistory()
      } else {
        console.error('Failed to delete comment:', await response.text())
        setError('Failed to delete comment')
      }
    } catch (error) {
      console.error('Error deleting comment:', error)
      setError('Failed to delete comment')
    }
  }

  const startEditingComment = (commentId: number, currentText: string) => {
    setEditingCommentId(commentId)
    setEditCommentText(currentText)
  }

  const cancelEditing = () => {
    setEditingCommentId(null)
    setEditCommentText('')
  }

  const handleTogglePin = async (commentId: number) => {
    if (!deal) return

    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/deals/${deal.id}/comments/${commentId}/pin`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        await fetchHistory()
      } else {
        console.error('Failed to toggle pin:', await response.text())
        setError('Failed to toggle pin status')
      }
    } catch (error) {
      console.error('Error toggling pin:', error)
      setError('Failed to toggle pin status')
    }
  }

  const handleAssignUser = async (userId: number, assignmentType: 'primary' | 'collaborator') => {
    if (!deal) return

    try {
      setAssignmentLoading(true)
      const response = await makeJsonApiCall(`api/deals/${deal.id}/assignments`, 'POST', {
        user_id: userId,
        assignment_type: assignmentType
      })

      if (response.ok) {
        await fetchAssignments()
        await fetchHistory()
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to assign user')
      }
    } catch (error) {
      console.error('Error assigning user:', error)
      setError('Failed to assign user')
    } finally {
      setAssignmentLoading(false)
    }
  }

  const handleRemoveAssignment = async (assignmentId: number) => {
    if (!deal) return

    try {
      setAssignmentLoading(true)
      const response = await makeApiCall(`api/deals/${deal.id}/assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })

      if (response.ok) {
        await fetchAssignments()
        await fetchHistory()
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to remove assignment')
      }
    } catch (error) {
      console.error('Error removing assignment:', error)
      setError('Failed to remove assignment')
    } finally {
      setAssignmentLoading(false)
    }
  }

  const formatCurrency = (amount: string | number) => {
    // Handle edge cases where amount might be an object or invalid value
    if (amount === null || amount === undefined || amount === '') {
      return '$0'
    }
    
    // If it's an object, try to extract a numeric value or default to 0
    if (typeof amount === 'object') {
      return '$0'
    }
    
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    
    // Check if the parsed value is a valid number
    if (isNaN(numAmount)) {
      return '$0'
    }
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getCommentTypeDisplay = (type: string) => {
    switch(type) {
      case 'internal_note': return t('deals.dealDetail.commentTypes.internal_note')
      case 'call': return t('deals.dealDetail.commentTypes.call')
      case 'text': return t('deals.dealDetail.commentTypes.text')
      case 'email': return t('deals.dealDetail.commentTypes.email')
      case 'in_person': return t('deals.dealDetail.commentTypes.in_person')
      case 'other': return t('deals.dealDetail.commentTypes.other')
      default: return t('deals.dealDetail.commentTypes.comment')
    }
  }

  const getCommentTypeColor = (type: string) => {
    switch(type) {
      case 'internal_note': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
      case 'call': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      case 'text': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      case 'email': return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300'
      case 'in_person': return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
      case 'other': return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
    }
  }

  // Helper functions for task completion modal
  const translatePriority = (priority: string) => {
    return t(`tasks.priorities.${priority}`)
  }

  const handleCompleteTask = async (taskId: number) => {
    // Find the task to get its details
    const task = dealTasks.find(t => t.id === taskId)
    if (!task) return
    
    // Open the completion modal instead of immediately completing
    setCompletionModal({ isOpen: true, task })
    setFollowUpDescription('')
    setFollowUpPriority('medium')
    setFollowUpSelectedDate('')
    setFollowUpCustomDate('')
  }

  const confirmTaskCompletion = async () => {
    if (!completionModal.task) return

    try {
      const token = localStorage.getItem('token')
      const response = await makeJsonApiCall(`api/tasks/${completionModal.task.id}`, 'PUT', {
        status: 'completed'
      })

      if (response.ok) {
        // Close modal and refresh
        setCompletionModal({ isOpen: false, task: null })
        setFollowUpDescription('')
        setFollowUpPriority('medium')
        setFollowUpSelectedDate('')
        setFollowUpCustomDate('')
        await fetchDealTasks()
      }
    } catch (error) {
      console.error('Error completing task:', error)
    }
  }

  const scheduleFollowUp = async () => {
    if (!completionModal.task || !followUpDescription.trim()) return

    try {
      const token = localStorage.getItem('token')
      let dueDate: string

      // Use custom date if provided, otherwise use selected date from buttons, otherwise default to tomorrow
      if (followUpCustomDate) {
        dueDate = followUpCustomDate
      } else if (followUpSelectedDate) {
        dueDate = followUpSelectedDate
      } else {
        // Default to tomorrow
        const today = new Date()
        const calculatedDate = new Date(today)
        calculatedDate.setDate(today.getDate() + 1)
        dueDate = formatLocalDate(calculatedDate)
      }

      // CRITICAL: Preserve ALL relationships from parent task
      const followUpData: any = {
        customer_id: completionModal.task.customer_id,
        parent_task_id: completionModal.task.id,
        description: followUpDescription,
        due_date: dueDate,
        priority: followUpPriority,
        status: 'pending',
        task_type: 'follow_up'
      }

      // Retain all associations (deal_id and lead_id)
      if (completionModal.task.deal_id) {
        followUpData.deal_id = completionModal.task.deal_id
      }
      if (completionModal.task.lead_id) {
        followUpData.lead_id = completionModal.task.lead_id
      }

      const response = await makeApiCall('api/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(followUpData)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.error || errorData.message || 'Failed to create follow-up task'
        console.error('Follow-up task creation error:', errorData)
        throw new Error(errorMessage)
      }

      // Complete the original task
      const completeResponse = await makeApiCall(`api/tasks/${completionModal.task.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: 'completed' })
      })

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json().catch(() => ({}))
        const errorMessage = errorData.error || errorData.message || 'Failed to complete original task'
        console.error('Task completion error:', errorData)
        throw new Error(errorMessage)
      }

      // Close modal and refresh
      setCompletionModal({ isOpen: false, task: null })
      setFollowUpDescription('')
      setFollowUpPriority('medium')
      setFollowUpSelectedDate('')
      setFollowUpCustomDate('')
      await fetchDealTasks()
    } catch (err: any) {
      console.error('Error creating follow-up:', err)
      alert(t('tasks.errors.followUpFailed', { 
        defaultValue: `Failed to create follow-up task: ${err.message || 'Unknown error'}` 
      }))
    }
  }

  const getDealStatusTimeline = () => {
    // Create timeline from deal statuses and history
    const statusChanges = history
      .filter(entry => entry.action_type === 'update' && entry.field_changed === 'status_id')
      .map(entry => ({
        status_name: typeof entry.new_values === 'string' ? entry.new_values : '',
        date: entry.created_at,
        isCurrent: false
      }))

    // Add current status if not in history
    if (!statusChanges.some(change => change.status_name === deal?.status_name)) {
      statusChanges.push({
        status_name: deal?.status_name || '',
        date: deal?.updated_at || '',
        isCurrent: true
      })
    }

    return statusChanges
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{t('deals.dealDetail.loading')}</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <XIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('deals.dealDetail.errorLoading')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </p>
          <button
            onClick={() => navigate('/dashboard/pipeline')}
            className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            {t('deals.dealDetail.backToDeals')}
          </button>
        </div>
      </div>
    )
  }

  // Deal not found
  if (!deal) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t('deals.dealDetail.dealNotFound')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t('deals.dealDetail.dealNotFoundMessage')}
          </p>
          <button
            onClick={() => navigate('/dashboard/pipeline')}
            className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            {t('deals.dealDetail.backToDeals')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <FullWidthContent>
      <div className="space-y-6">
        {/* Page Header */}
        {(() => {
          const allPeople = dealCustomers.length > 0
            ? [
                ...dealCustomers.filter(dc => dc.role === 'applicant').map(dc => ({ ...dc, isApplicant: true })),
                ...dealCustomers.filter(dc => dc.role === 'co_applicant').map(dc => ({ ...dc, isApplicant: false })),
              ]
            : [{
                id: 0,
                customer_id: deal.customer_id,
                name: deal.customer_name,
                email: deal.customer_email,
                phone: deal.customer_phone,
                role: 'applicant' as const,
                order_position: 0,
                isApplicant: true,
              }]
          const primaryApplicant = allPeople[0]
          const coApplicants = allPeople.slice(1)
          return (
            <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Top bar: names + actions */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700/60">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0 mr-4">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: deal.status_color }}
                  />
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                    <Link
                      to={`/dashboard/customers/${primaryApplicant.customer_id}`}
                      className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                    >
                      {primaryApplicant.name}
                    </Link>
                  </h1>
                  {coApplicants.map(co => (
                    <span key={co.customer_id} className="flex items-center gap-1.5">
                      <span className="text-gray-400 dark:text-gray-500 font-medium">+</span>
                      <Link
                        to={`/dashboard/customers/${co.customer_id}`}
                        className="text-base font-semibold text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                      >
                        {co.name}
                      </Link>
                    </span>
                  ))}
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full text-white flex-shrink-0"
                    style={{ backgroundColor: deal.status_color }}
                  >
                    {deal.status_name}
                  </span>
                  {deal.board_name && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                      {deal.board_type === 'lead' ? '🎯 ' : ''}{deal.board_name}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {deal.board_type === 'lead' && deal.paired_board_id && (
                    <button
                      onClick={async () => {
                        if (!window.confirm('Move this lead to the deal board?')) return
                        const token = localStorage.getItem('token')
                        const res = await makeApiCall(`api/deals/${id}/send-to-deal-board`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` }
                        })
                        if (res.ok) {
                          await fetchDealData()
                        } else {
                          const err = await res.json()
                          alert(err.error || 'Failed to move to deal board')
                        }
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                      Send to Deal Board
                    </button>
                  )}
                  {assignments.length > 0 && (
                    <UserAvatarStack
                      users={assignments.map(assignment => ({
                        id: assignment.user_id,
                        first_name: assignment.first_name,
                        last_name: assignment.last_name,
                        email: assignment.email,
                        profile_picture_url: assignment.profile_picture_url,
                        assignment_type: assignment.assignment_type
                      }))}
                      maxVisible={3}
                      size="sm"
                    />
                  )}
                  <button
                    onClick={() => setShowAssignmentModal(true)}
                    className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-200 dark:border-gray-600"
                  >
                    {assignments.length > 0 ? t('deals.dealDetail.manage') : t('deals.dealDetail.assignTeam')}
                  </button>
                  <button
                    onClick={() => setShowToolsModal(true)}
                    className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>{t('deals.dealDetail.tools')}</span>
                  </button>
                </div>
              </div>

              {/* Bottom: applicant contact cards + financial stats */}
              <div className="flex items-stretch divide-x divide-gray-100 dark:divide-gray-700/60">
                {/* Applicants section */}
                <div className="flex-1 px-5 py-3.5 space-y-2.5 min-w-0">
                  {allPeople.map(person => {
                    const initials = person.name.split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase()
                    const emailKey = `email-${person.customer_id}`
                    const phoneKey = `phone-${person.customer_id}`
                    return (
                      <div key={person.customer_id} className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 ${person.isApplicant ? 'bg-indigo-500' : 'bg-violet-500'}`}>
                          {initials}
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                          <Link
                            to={`/dashboard/customers/${person.customer_id}`}
                            className="text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                          >
                            {person.name}
                          </Link>
                          <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full ${person.isApplicant ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300' : 'bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300'}`}>
                            {person.isApplicant ? 'Applicant' : 'Co-applicant'}
                          </span>
                          {person.email && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800/50">
                              <EmailIcon className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                              <span className="text-xs text-gray-700 dark:text-gray-300 leading-none">{person.email}</span>
                              <button
                                onClick={() => copyToClipboard(person.email!, emailKey)}
                                className="p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors"
                                title={t('deals.copy')}
                              >
                                <CopyIcon className={`h-3 w-3 transition-colors ${copiedItem === emailKey ? 'text-emerald-500' : 'text-blue-400 dark:text-blue-500'}`} />
                              </button>
                            </div>
                          )}
                          {person.phone && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800/50">
                              <PhoneIcon className="h-3.5 w-3.5 text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
                              <span className="text-xs text-gray-700 dark:text-gray-300 leading-none">{formatPhoneNumber(person.phone)}</span>
                              <button
                                onClick={() => copyToClipboard(formatPhoneNumber(person.phone) || '', phoneKey)}
                                className="p-0.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-800 transition-colors"
                                title={t('deals.copy')}
                              >
                                <CopyIcon className={`h-3 w-3 transition-colors ${copiedItem === phoneKey ? 'text-emerald-500' : 'text-emerald-400 dark:text-emerald-500'}`} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                  {partnerNames.length > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 pt-0.5 pl-11">
                      <span className="font-medium">{t('deals.dealDetail.referredBy')}:</span>
                      <span>{partnerNames.join(', ')}</span>
                    </div>
                  )}
                </div>

                {/* Financial stats */}
                <div className="flex items-center gap-6 px-6 py-3.5 flex-shrink-0">
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                      Loan Amount
                    </span>
                    <div className="flex items-baseline gap-0.5">
                      <span className="text-sm font-semibold text-gray-400 dark:text-gray-500">$</span>
                      <input
                        type="number"
                        step="1"
                        value={deal.amount ? Math.round(Number(deal.amount)) : ''}
                        onChange={(e) => setDeal({ ...deal, amount: String(Math.round(Number(e.target.value))) })}
                        onBlur={(e) => updateDealField('amount', String(Math.round(Number(e.target.value))))}
                        className="text-2xl font-bold bg-transparent border-none text-gray-900 dark:text-gray-100 focus:ring-0 focus:outline-none w-36 p-0 leading-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="0"
                      />
                    </div>
                  </div>

                  {commission && (
                    <>
                      <div className="w-px h-10 bg-gray-200 dark:bg-gray-600 flex-shrink-0" />
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                          {t('deals.netCommission')}
                        </span>
                        <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 leading-none">
                          {formatCurrency(
                            commission.netCommission - commissionSplits.reduce((sum, split) => {
                              const amount = split.split_type === 'percentage'
                                ? (commission.netCommission * (split.split_value / 100))
                                : split.split_value
                              return sum + amount
                            }, 0)
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )
        })()}

      {/* Archived Deal Status Dropdown - Only shows for archived deals */}
      {deal?.status_is_archived && (
        <div 
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border-2 border-orange-400 dark:border-orange-500 p-4 mb-4"
          style={{ backgroundColor: deal.status_color ? `${deal.status_color}20` : '#fee2e2' }}
        >
          <div className="flex items-center space-x-3">
            <div 
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: deal.status_color }}
            />
            <span className="font-semibold text-gray-900 dark:text-white">{t('dealDetail.archivedDealChangeStatus')}</span>
            <select
              value={deal.status_id || ''}
              onChange={async (e) => {
                const newStatusId = e.target.value
                
                if (!newStatusId) {
                  return
                }
                
                await updateDealField('status_id', newStatusId)
              }}
              className="flex-1 px-4 py-2 border-2 border-orange-400 dark:border-orange-500 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
            >
              <option value="">{t('dealDetail.selectStatus')}</option>
              {dealStatuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name} {status.is_archived ? t('dealDetail.archivedLabel') : ''}
                </option>
              ))}
            </select>
            <span className="text-sm text-orange-600 dark:text-orange-400 font-medium whitespace-nowrap">
              {t('dealDetail.selectNonArchivedToReopen')}
            </span>
          </div>
        </div>
      )}

      {/* Deal Status Timeline */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="relative">
          <div className="flex h-8 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
            {(() => {
              const currentStatusIndex = dealStatuses.findIndex(s => String(s.id) === String(deal?.status_id))
              const currentStatus = dealStatuses[currentStatusIndex]
              
              // Group completed + current into one large arrow
              const segments: React.JSX.Element[] = []
              
              // Combined progress arrow (all past + current)
              if (currentStatusIndex >= 0) {
                segments.push(
                  <div
                    key="progress"
                    className="relative group"
                    style={{ 
                      flex: currentStatusIndex + 1,
                      zIndex: 50, // High z-index for progress to overlap on first future
                    }}
                  >
                    <div
                      className="h-full flex items-center justify-center relative transition-all duration-300 px-3 pr-4"
                      style={{
                        backgroundColor: currentStatus?.color,
                        clipPath: currentStatusIndex === dealStatuses.length - 1
                          ? 'none'
                          : 'polygon(0 0, calc(100% - 12px) 0, calc(100% - 4px) 50%, calc(100% - 12px) 100%, 0 100%)'
                      }}
                    >
                      <span className="text-xs font-semibold z-10 text-white truncate">
                        {currentStatus?.name}
                      </span>
                    </div>
                    {/* Tooltip */}
                    <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-[10px] text-gray-600 dark:text-gray-400 pointer-events-none z-10">
                      {currentStatus?.name}
                    </div>
                  </div>
                )
              }
              
              // Future statuses (individual) - Now clickable with filtered appearance
              for (let index = currentStatusIndex + 1; index < dealStatuses.length; index++) {
                const status = dealStatuses[index]
                const isLast = index === dealStatuses.length - 1
                const zIndex = 49 - (index - currentStatusIndex)
                
                segments.push(
                  <div
                    key={status.id}
                    className="relative flex-1 group cursor-pointer"
                    style={{
                      zIndex: zIndex,
                      marginLeft: '-12px',
                      filter: 'saturate(0.3) brightness(1.2)', // Desaturate and brighten future statuses
                    }}
                    onClick={() => {
                      setSelectedNewStatus(status)
                      performStatusChange(status)
                    }}
                  >
                    <div
                      className="h-full flex items-center justify-center relative transition-all duration-300 pl-5 pr-3 hover:brightness-110"
                      style={{
                        backgroundColor: status.color, // Use actual status color
                        clipPath: isLast
                          ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%)' // Straight left and right for last
                          : 'polygon(0 0, calc(100% - 12px) 0, calc(100% - 4px) 50%, calc(100% - 12px) 100%, 0 100%)' // Straight left, arrow right
                      }}
                    >
                      <span className="text-[10px] font-semibold z-10 text-white truncate">
                        {status.name}
                      </span>
                    </div>
                    {/* Tooltip */}
                    <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap text-[10px] text-gray-600 dark:text-gray-400 pointer-events-none z-10">
                      Click to move to {status.name}
                    </div>
                  </div>
                )
              }
              
              return segments
            })()}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - History & Comments */}
        <div className="lg:col-span-2 space-y-6">
          {/* Multi-function Section with Tabs */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 h-fit">
            {/* Tab Header */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between px-6 pt-6 pb-2">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {activeTab === 'history' ? t('deals.dealDetail.historyComments') : activeTab === 'sms' ? t('deals.dealDetail.sms') : activeTab === 'compliance' ? t('deals.dealDetail.compliance') : 'Email'}
                </h3>
              </div>
              {/* Tab Switcher */}
              <div className="flex space-x-1 px-6">
                <button
                  onClick={() => setActiveTab('history')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === 'history'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-600 border-b-2 border-gray-900 dark:border-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  <span>{t('deals.dealDetail.historyComments')}</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('sms')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === 'sms'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-600 border-b-2 border-gray-900 dark:border-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  <span>{t('deals.dealDetail.sms')}</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('email')}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === 'email'
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-600 border-b-2 border-gray-900 dark:border-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Email</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('compliance')}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === 'compliance'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-600 border-b-2 border-gray-900 dark:border-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{t('deals.dealDetail.compliance')}</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'history' && (
              <>
            {/* Add Comment Section - Redesigned */}
            <div className="border-b border-gray-200 dark:border-gray-700 p-5">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                {/* Comment textarea with integrated header */}
                <div className="relative bg-white dark:bg-gray-900 rounded-t-lg">
                  <textarea
                    rows={3}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={t('deals.dealDetail.addNotePlaceholder')}
                    className="w-full px-4 py-3 text-sm border-0 rounded-t-lg bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-0 focus:outline-none resize-none"
                  />
                </div>

                {/* Footer with controls */}
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
                  <div className="flex items-center space-x-2">
                    {/* Comment type selector */}
                    <select
                      value={commentType}
                      onChange={(e) => setCommentType(e.target.value)}
                      className="text-xs px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-colors"
                    >
                      <option value="comment">{t('deals.dealDetail.commentTypes.comment')}</option>
                      <option value="internal_note">{t('deals.dealDetail.commentTypes.internal_note')}</option>
                      <option value="call">{t('deals.dealDetail.commentTypes.call')}</option>
                      <option value="text">{t('deals.dealDetail.commentTypes.text')}</option>
                      <option value="email">{t('deals.dealDetail.commentTypes.email')}</option>
                      <option value="in_person">{t('deals.dealDetail.commentTypes.in_person')}</option>
                      <option value="other">{t('deals.dealDetail.commentTypes.other')}</option>
                    </select>
                  </div>

                  {/* Submit button */}
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || addingComment}
                    className="px-4 py-1.5 bg-gray-900 text-white text-sm rounded-md font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center shadow-sm"
                  >
                    {addingComment ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5"></div>
                        {t('deals.dealDetail.addingComment')}
                      </>
                    ) : (
                      <>
                        <MessageIcon size={14} className="mr-1.5" />
                        {t('deals.dealDetail.addComment')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="max-h-[800px] overflow-y-auto widget-scrollbar">
              <div className="p-6 space-y-6">
                {history && Array.isArray(history) && history.filter(e => e.action_type === 'comment').length > 0 ? (
                  history.filter(e => e.action_type === 'comment').map((entry) => {
                    return (
                      <div key={entry.id} className="relative pl-8 pb-8 border-l-2 border-gray-200 dark:border-gray-600 last:border-l-0">
                        {/* Timeline dot */}
                        <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-white dark:bg-gray-800 border-2 border-gray-900 dark:border-blue-400"></div>
                        
                        {/* Content */}
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                          {/* Header with user info, date, and status */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">
                                  {(entry.first_name && entry.last_name 
                                    ? `${entry.first_name[0]}${entry.last_name[0]}`
                                    : entry.created_by_username?.[0]?.toUpperCase() || 'U')}
                                </span>
                              </div>
                              <div>
                                <div className="flex items-center space-x-2">
                                  <p className="font-semibold text-gray-900 dark:text-white">
                                    {entry.first_name && entry.last_name 
                                      ? `${entry.first_name} ${entry.last_name}`
                                      : entry.created_by_username || 'Unknown User'}
                                  </p>
                                  {/* Comment type badge - inline with name */}
                                  {entry.action_type === 'comment' && (
                                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${getCommentTypeColor(entry.comment_type || 'comment')}`}>
                                      {getCommentTypeDisplay(entry.comment_type || 'comment')}
                                    </span>
                                  )}
                                  {/* Pinned indicator */}
                                  {entry.is_pinned && (
                                    <span className="inline-flex items-center space-x-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" />
                                      </svg>
                                      <span>{t('deals.dealDetail.pinnedComment')}</span>
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">
                                  {formatDate(entry.created_at)}
                                </p>
                              </div>
                            </div>
                            
                            {/* Deal status at time of comment - now using the actual stored status */}
                            {entry.status_name_at_time && (
                              <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-600">
                                <div 
                                  className="w-2 h-2 rounded-full" 
                                  style={{ backgroundColor: entry.status_color_at_time || '#3B82F6' }}
                                ></div>
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                  {entry.status_name_at_time}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Content */}
                          <div className="text-gray-700 dark:text-gray-300">
                            {entry.action_type === 'update' ? (
                              <>
                                <div className="font-medium text-gray-900 dark:text-gray-600 mb-2">
                                  Updated {entry.field_changed}
                                </div>
                                {entry.previous_values && entry.new_values && (
                                  <div className="text-sm bg-white dark:bg-gray-800 p-3 rounded border">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <span className="font-medium text-red-600 dark:text-red-400">From:</span>
                                        <p className="mt-1">
                                          {typeof entry.previous_values === 'object' ? JSON.stringify(entry.previous_values) : entry.previous_values}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-green-600 dark:text-green-400">To:</span>
                                        <p className="mt-1">
                                          {typeof entry.new_values === 'object' ? JSON.stringify(entry.new_values) : entry.new_values}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                {editingCommentId === entry.id ? (
                                  // Edit mode
                                  <div className="space-y-3">
                                    <textarea
                                      value={editCommentText}
                                      onChange={(e) => setEditCommentText(e.target.value)}
                                      rows={4}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none"
                                    />
                                    <div className="flex items-center space-x-2">
                                      <button
                                        onClick={() => handleEditComment(entry.id)}
                                        className="px-3 py-1.5 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-800 transition-colors"
                                      >
                                        Save
                                      </button>
                                      <button
                                        onClick={cancelEditing}
                                        className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  // View mode
                                  <div className="group">
                                    <div className="whitespace-pre-wrap leading-relaxed">
                                      {entry.comment}
                                    </div>
                                    {/* Pin, Edit and Delete buttons - only show for comments */}
                                    <div className="flex items-center space-x-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button
                                        onClick={() => handleTogglePin(entry.id)}
                                        className={`flex items-center space-x-1 px-2 py-1 text-xs rounded transition-colors ${
                                          entry.is_pinned 
                                            ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20' 
                                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                        }`}
                                        title={entry.is_pinned ? t('deals.dealDetail.unpinComment') : t('deals.dealDetail.pinComment')}
                                      >
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L9 4.323V3a1 1 0 011-1z" />
                                        </svg>
                                        <span>{entry.is_pinned ? t('deals.dealDetail.unpinComment') : t('deals.dealDetail.pinComment')}</span>
                                      </button>
                                      <button
                                        onClick={() => startEditingComment(entry.id, entry.comment || '')}
                                        className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-900 dark:text-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                      >
                                        <EditIcon size={12} />
                                        <span>Edit</span>
                                      </button>
                                      <button
                                        onClick={() => {
                                          setDeletingCommentId(entry.id)
                                          setShowDeleteModal(true)
                                        }}
                                        className="flex items-center space-x-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                      >
                                        <XIcon className="w-3 h-3" />
                                        <span>Delete</span>
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No History Yet
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Start the conversation by adding the first comment above.
                    </p>
                  </div>
                )}
              </div>
            </div>
              </>
            )}

            {/* SMS Tab Content */}
            {activeTab === 'sms' && (
              <div className="p-6">
                {dealCustomers.length > 0 ? (
                  <SMSClient 
                    dealId={deal.id} 
                    customers={dealCustomers}
                  />
                ) : (
                  <div className="text-center py-16">
                    <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-12 h-12 text-gray-900 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {t('sms.noCustomers')}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                      {t('sms.noCustomersDescription')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Email Tab Content */}
            {activeTab === 'email' && (
              <div className="p-6">
                <div className="text-center py-16">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-12 h-12 text-gray-900 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Email Integration Coming Soon
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                    This section will allow you to send and manage emails directly from this deal. Stay tuned for updates!
                  </p>
                  <div className="mt-6 inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">Feature in Development</span>
                  </div>
                </div>
              </div>
            )}

            {/* Compliance Tab Content */}
            {activeTab === 'compliance' && (
              <div className="p-6">
                <div className="text-center py-16">
                  <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg className="w-12 h-12 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {t('deals.dealDetail.complianceComingSoon')}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                    {t('deals.dealDetail.complianceDescription')}
                  </p>
                  <div className="mt-6 inline-flex items-center space-x-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg">
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">Feature in Development</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Tasks and Summary */}
        <div className="space-y-6">
          {/* Associated Tasks - Compact */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
              {t('deals.dealDetail.associatedTasks')}
            </h3>
            
            {tasksLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Current Due Task */}
                {dealTasks.filter(task => task.status !== 'completed' && task.status !== 'cancelled').length > 0 && (
                  <div>
                    {dealTasks
                      .filter(task => task.status !== 'completed' && task.status !== 'cancelled')
                      .slice(0, 1)
                      .map(task => (
                        <div key={task.id} className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 dark:text-white">{task.description}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {t('deals.dealDetail.due')} {new Date(task.due_date).toLocaleDateString()} • {t('deals.dealDetail.priority')} {task.priority}
                            </p>
                          </div>
                          <button
                            onClick={() => handleCompleteTask(task.id)}
                            className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                          >
                            {t('deals.dealDetail.complete')}
                          </button>
                        </div>
                      ))}
                  </div>
                )}

                {dealTasks.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400 mb-4">{t('deals.dealDetail.noTasksAssociated')}</p>
                    <button
                      onClick={() => setShowCreateTaskModal(true)}
                      className="inline-flex items-center px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t('deals.dealDetail.createTask')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Compact Commission Summary */}
          {commission && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('deals.dealDetail.commissionSummary')}
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="text-center">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('deals.dealDetail.baseBps')}</div>
                  <input
                    type="number"
                    value={deal.base_points || 0}
                    onChange={(e) => setDeal({ ...deal, base_points: e.target.value })}
                    onBlur={(e) => updateDealField('base_points', e.target.value)}
                    className="w-full text-center font-semibold text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('deals.dealDetail.bonusBps')}</div>
                  <input
                    type="number"
                    value={deal.bonus_points || 0}
                    onChange={(e) => setDeal({ ...deal, bonus_points: e.target.value })}
                    onBlur={(e) => updateDealField('bonus_points', e.target.value)}
                    className="w-full text-center font-semibold text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('deals.dealDetail.volumeBps')}</div>
                  <input
                    type="number"
                    value={deal.volume_points || 0}
                    onChange={(e) => setDeal({ ...deal, volume_points: e.target.value })}
                    onBlur={(e) => updateDealField('volume_points', e.target.value)}
                    className="w-full text-center font-semibold text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('deals.dealDetail.totalBps')}</div>
                  <div className="font-semibold text-gray-900 dark:text-gray-600 py-1">{commission.totalPoints || 0}</div>
                </div>
              </div>

              {/* Commission Splits Section - Inline Editing */}
              {(commissionSplits.length > 0 || editingNewSplit) && (
                <div className="mb-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('deals.dealDetail.commissionSplits')}</span>
                    {!editingNewSplit && (
                      <button
                        onClick={() => setEditingNewSplit(true)}
                        className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      >
                        <PlusIcon className="w-3 h-3 inline mr-1" />
                        {t('common.add')}
                      </button>
                    )}
                  </div>
                  
                  {/* Existing Splits */}
                  {commissionSplits.map((split) => {
                    const calculatedAmount = split.split_type === 'percentage' 
                      ? (commission.netCommission * (split.split_value / 100))
                      : split.split_value
                    
                    return (
                      <div key={split.id} className="flex justify-between items-center text-sm p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                        <div className="flex-1">
                          <span className="text-gray-900 dark:text-white">
                            {split.partner_name || `${split.partner_first_name} ${split.partner_last_name}`}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 text-xs ml-2">
                            ({split.split_type === 'percentage' ? `${split.split_value}%` : formatCurrency(split.split_value)})
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-red-600 dark:text-red-400">
                            -{formatCurrency(calculatedAmount)}
                          </span>
                          <button
                            onClick={() => handleDeleteSplit(split.id)}
                            className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          >
                            <XIcon className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    )
                  })}

                  {/* New Split Inline Editor */}
                  {editingNewSplit && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-700 rounded-lg space-y-3">
                      {/* Partner Search */}
                      <div className="relative">
                        <input
                          type="text"
                          value={partnerSearchQuery}
                          onChange={(e) => {
                            setPartnerSearchQuery(e.target.value)
                            searchPartners(e.target.value)
                            setShowPartnerDropdown(true)
                          }}
                          onFocus={() => {
                            if (partnerSearchQuery) {
                              searchPartners(partnerSearchQuery)
                            }
                            setShowPartnerDropdown(true)
                          }}
                          placeholder={newSplitData.partner ? newSplitData.partner.name || `${newSplitData.partner.first_name} ${newSplitData.partner.last_name}` : t('deals.dealDetail.searchPartner')}
                          className="w-full text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                        
                        {/* Partner Dropdown */}
                        {showPartnerDropdown && (partnerSearchResults.length > 0 || partnerSearchQuery.trim()) && (
                          <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto widget-scrollbar">
                            {partnerSearchResults.length === 0 && partnerSearchQuery.trim() && (
                              <button
                                type="button"
                                onClick={() => {
                                  setShowPartnerDropdown(false)
                                  setShowPartnerCreationModal(true)
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 flex items-center gap-2 text-blue-600 dark:text-blue-400"
                              >
                                <PlusIcon className="w-3 h-3" />
                                {t('deals.dealDetail.createPartner', { name: partnerSearchQuery })}
                              </button>
                            )}
                            {partnerSearchResults.map((partner) => (
                              <button
                                key={partner.id}
                                type="button"
                                onClick={() => {
                                  // Auto-populate commission cut if partner has one set
                                  const updatedSplitData = {
                                    partner,
                                    split_type: partner.commission_cut ? 'percentage' as const : newSplitData.split_type,
                                    split_value: partner.commission_cut ? partner.commission_cut.toString() : newSplitData.split_value,
                                    notes: newSplitData.notes
                                  }
                                  setNewSplitData(updatedSplitData)
                                  setPartnerSearchQuery(partner.name || `${partner.first_name} ${partner.last_name}`)
                                  setShowPartnerDropdown(false)
                                }}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 border-b border-gray-200 dark:border-gray-600 last:border-0"
                              >
                                <div className="font-medium text-gray-900 dark:text-white">
                                  {partner.name || `${partner.first_name} ${partner.last_name}`}
                                  {partner.commission_cut && (
                                    <span className="ml-2 text-xs text-blue-600 dark:text-blue-400">
                                      ({partner.commission_cut}% default)
                                    </span>
                                  )}
                                </div>
                                {partner.email && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">{partner.email}</div>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Split Type and Value */}
                      <div className="flex gap-2">
                        <select
                          value={newSplitData.split_type}
                          onChange={(e) => setNewSplitData({ ...newSplitData, split_type: e.target.value as 'percentage' | 'fixed_amount' })}
                          className="text-sm px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="percentage">%</option>
                          <option value="fixed_amount">$</option>
                        </select>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newSplitData.split_value}
                          onChange={(e) => setNewSplitData({ ...newSplitData, split_value: e.target.value })}
                          placeholder={newSplitData.split_type === 'percentage' ? '0.00' : '0.00'}
                          className="flex-1 text-sm px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={handleCancelNewSplit}
                          className="flex-1 text-xs px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          {t('common.cancel')}
                        </button>
                        <button
                          onClick={handleSaveNewSplit}
                          className="flex-1 text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          {t('common.save')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">{t('deals.dealDetail.netCommission')}</span>
                  {commissionSplits.length === 0 && !editingNewSplit && (
                    <button
                      onClick={() => setEditingNewSplit(true)}
                      className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-800/50 text-green-700 dark:text-green-300 rounded hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                    >
                      <PlusIcon className="w-3 h-3 inline mr-1" />
                      {t('deals.dealDetail.addCommissionSplit')}
                    </button>
                  )}
                </div>
                <span className="font-bold text-green-600 dark:text-green-400">
                  {formatCurrency(
                    commission.netCommission - commissionSplits.reduce((sum, split) => {
                      const amount = split.split_type === 'percentage'
                        ? (commission.netCommission * (split.split_value / 100))
                        : split.split_value
                      return sum + amount
                    }, 0)
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Deal Summary Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('deals.dealDetail.dealSummary')}
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('deals.dealDetail.dealType')}</span>
                <select
                  value={deal.deal_type_id || ''}
                  onChange={(e) => {
                    setDeal({ ...deal, deal_type_id: e.target.value })
                    updateDealField('deal_type_id', e.target.value)
                  }}
                  className="text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:ring-2 focus:ring-gray-900 focus:border-transparent w-48"
                >
                  <option value="">{t('deals.dealDetail.notSpecified')}</option>
                  {dealTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('deals.dealDetail.dealVersion')}</span>
                <select
                  value={deal.deal_version || ''}
                  onChange={(e) => {
                    setDeal({ ...deal, deal_version: e.target.value as 'commercial' | 'residential' })
                    updateDealField('deal_version', e.target.value)
                  }}
                  className="text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:ring-2 focus:ring-gray-900 focus:border-transparent w-48"
                >
                  <option value="">Not Set</option>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                </select>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('deals.dealDetail.lender')}</span>
                <div className="relative w-48 flex items-center gap-1">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={showLenderDropdown ? lenderSearchQuery : (deal.lender_name || '')}
                      onChange={(e) => {
                        setLenderSearchQuery(e.target.value)
                        searchLenders(e.target.value)
                      }}
                      onFocus={() => {
                        setShowLenderDropdown(true)
                        setLenderSearchQuery('')
                        searchLenders('')
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowLenderDropdown(false), 200)
                      }}
                      placeholder={t('deals.dealDetail.searchLender')}
                      className="text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:ring-2 focus:ring-gray-900 focus:border-transparent w-full"
                    />
                    
                    {/* Dropdown */}
                    {showLenderDropdown && (
                      <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto widget-scrollbar">
                        {isSearchingLenders ? (
                          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            {t('common.loading')}
                          </div>
                        ) : lenderSearchResults.length > 0 ? (
                          <>
                            {lenderSearchResults.map((lender) => (
                              <button
                                key={lender.id}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault()
                                  handleSelectLender(lender)
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                              >
                                {lender.name}
                              </button>
                            ))}
                          </>
                        ) : lenderSearchQuery.trim() ? (
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault()
                              handleCreateLender()
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                          >
                            {t('deals.dealDetail.createLender', { name: lenderSearchQuery })}
                          </button>
                        ) : (
                          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                            {t('deals.dealDetail.searchLender')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Clear lender button — only shown when a lender is set */}
                  {deal.lender_id && !showLenderDropdown && (
                    <button
                      type="button"
                      title={t('deals.dealDetail.removeLender') || 'Remove lender'}
                      onClick={async () => {
                        await updateDealField('lender_id', null)
                        setDeal(prev => prev ? { ...prev, lender_id: null, lender_name: null } : prev)
                      }}
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    >
                      <XIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('deals.dealDetail.created')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatDate(deal.created_at)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('deals.dealDetail.lastUpdated')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatDate(deal.updated_at)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('deals.dealDetail.expectedClose')}</span>
                <input
                  type="date"
                  value={deal.expected_close_date ? new Date(deal.expected_close_date).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    setDeal({ ...deal, expected_close_date: e.target.value })
                    updateDealField('expected_close_date', e.target.value)
                  }}
                  className="text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:ring-2 focus:ring-gray-900 focus:border-transparent w-48"
                />
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('deals.dealDetail.actualClose')}</span>
                <input
                  type="date"
                  value={deal.closing_date ? new Date(deal.closing_date).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    setDeal({ ...deal, closing_date: e.target.value })
                    updateDealField('closing_date', e.target.value)
                  }}
                  className="text-sm font-medium text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:ring-2 focus:ring-gray-900 focus:border-transparent w-48"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Move Modal */}
      {showWorkflowMove && workflowInstance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto widget-scrollbar">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Move Workflow Step
              </h2>
              <button
                onClick={() => {
                  setShowWorkflowMove(false)
                  setSelectedTargetStep(null)
                  setWorkflowComment('')
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Current Step
                  </label>
                  <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: workflowInstance.current_step?.color }}
                      ></div>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {workflowInstance.current_step?.name}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Move to Step <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedTargetStep?.id || ''}
                    onChange={(e) => {
                      const stepId = parseInt(e.target.value)
                      const step = workflowInstance.all_steps.find(s => s.id === stepId)
                      setSelectedTargetStep(step || null)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                  >
                    <option value="">Select target step</option>
                    {workflowInstance.all_steps
                      .filter(step => step.id !== workflowInstance.current_step?.id)
                      .map((step) => (
                        <option key={step.id} value={step.id}>
                          {step.name} {step.is_end_step ? '(End)' : ''}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Comment (Optional)
                  </label>
                  <textarea
                    value={workflowComment}
                    onChange={(e) => setWorkflowComment(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="Optional reason for moving to this step..."
                  />
                </div>
              </form>
            </div>
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowWorkflowMove(false)
                  setSelectedTargetStep(null)
                  setWorkflowComment('')
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleWorkflowMove}
                disabled={!selectedTargetStep || workflowLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {workflowLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Moving...</span>
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    <span>Move Step</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tools Modal */}
      {showToolsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-gray-900">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {t('deals.dealDetail.toolsModal.title')}
                </h2>
                <p className="text-indigo-100 text-sm mt-1">
                  {t('deals.dealDetail.toolsModal.subtitle')}
                </p>
              </div>
              <button
                onClick={() => setShowToolsModal(false)}
                className="text-white hover:text-indigo-200 transition-colors"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto widget-scrollbar p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* PDF Export Tool */}
                <div className="bg-white dark:bg-gray-700 rounded-lg border-2 border-gray-200 dark:border-gray-600 p-6 hover:border-indigo-500 dark:hover:border-indigo-400 transition-colors">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-pink-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('deals.dealDetail.toolsModal.exportPdf.title')}
                    </h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {t('deals.dealDetail.toolsModal.exportPdf.description')}
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('deals.dealDetail.toolsModal.exportPdf.selectLanguage')}
                      </label>
                      <select
                        value={pdfLanguage}
                        onChange={(e) => setPdfLanguage(e.target.value as 'en' | 'fr')}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 dark:text-white"
                      >
                        <option value="en">{t('deals.dealDetail.toolsModal.exportPdf.english')}</option>
                        <option value="fr">{t('deals.dealDetail.toolsModal.exportPdf.french')}</option>
                      </select>
                    </div>
                    
                    {deal && currentUser ? (
                      <PDFDownloadLink
                        document={
                          <DealPDF
                            deal={deal}
                            user={currentUser}
                            customer={{
                              id: deal.customer_id,
                              name: deal.customer_name,
                              email: deal.customer_email,
                              phone: deal.customer_phone
                            }}
                            persons={customerPersons}
                            history={history.filter(e => e.action_type === 'comment')}
                            smsMessages={smsMessages}
                            language={pdfLanguage}
                          />
                        }
                        fileName={`deal-${deal.name || deal.title}-${new Date().toISOString().split('T')[0]}.pdf`}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg hover:from-red-600 hover:to-pink-700 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
                      >
                        {({ loading }) => (
                          <>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>
                              {loading 
                                ? t('deals.dealDetail.toolsModal.exportPdf.generating')
                                : t('deals.dealDetail.toolsModal.exportPdf.downloadPdf')
                              }
                            </span>
                          </>
                        )}
                      </PDFDownloadLink>
                    ) : (
                      <button
                        disabled
                        className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 rounded-lg font-medium cursor-not-allowed"
                      >
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
                        <span>Loading...</span>
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Example tool card structure (commented out for future use)
                <div className="bg-white dark:bg-gray-700 rounded-lg border-2 border-gray-200 dark:border-gray-600 p-6 hover:border-indigo-500 dark:hover:border-indigo-400 transition-colors cursor-pointer">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Tool Name</h4>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Description of what this tool does
                  </p>
                </div>
                */}
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={() => setShowToolsModal(false)}
                className="px-6 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t('deals.dealDetail.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deal Status Rules — Missing Required Fields Modal */}
      {showDealValidationModal && dealValidationState && deal && (
        <DealValidationModal
          isOpen={showDealValidationModal}
          dealId={deal.id}
          dealData={deal as unknown as Record<string, any>}
          missingFields={dealValidationState.missingFields}
          targetStatusId={dealValidationState.targetStatusId}
          targetStatusName={dealValidationState.targetStatusName}
          onSuccess={() => {
            setShowDealValidationModal(false)
            setDealValidationState(null)
            setSelectedNewStatus(null)
            fetchDealData()
          }}
          onClose={() => {
            setShowDealValidationModal(false)
            setDealValidationState(null)
          }}
        />
      )}

      {/* Delete Comment Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Delete Comment
              </h2>
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeletingCommentId(null)
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    Are you sure you want to delete this comment? This action cannot be undone.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    The comment will be permanently removed from the deal history.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeletingCommentId(null)
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteComment}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Comment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Management Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto widget-scrollbar">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('deals.dealDetail.assignments.manageDealAssignments')}
              </h2>
              <button
                onClick={() => setShowAssignmentModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Current Assignments */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {t('deals.dealDetail.assignments.currentAssignments')} ({assignments.length})
                </h3>
                
                {assignments.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    No team members assigned to this deal yet.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {assignments.map((assignment) => (
                      <div key={assignment.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-gray-800 to-gray-900 flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {assignment.first_name[0]}{assignment.last_name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {assignment.first_name} {assignment.last_name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {assignment.assignment_type === 'primary' ? t('deals.dealDetail.assignments.primaryAssignee') : t('deals.dealDetail.assignments.collaborator')}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveAssignment(assignment.id)}
                          disabled={assignmentLoading}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm disabled:opacity-50"
                        >
                          {t('deals.dealDetail.assignments.remove')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add New Assignment */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {t('deals.dealDetail.assignments.addTeamMember')}
                </h3>
                
                {teamMembers.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    {t('deals.dealDetail.assignments.noTeamMembersAvailable')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {teamMembers
                      .filter(member => !assignments.find(a => a.user_id === member.user_id))
                      .map((member) => (
                        <div key={member.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-gray-500 to-gray-600 flex items-center justify-center">
                              <span className="text-white font-semibold text-sm">
                                {member.first_name[0]}{member.last_name[0]}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {member.first_name} {member.last_name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {member.email}
                              </p>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleAssignUser(member.user_id, 'collaborator')}
                              disabled={assignmentLoading}
                              className="px-3 py-1 text-xs bg-gray-900 text-white rounded hover:bg-gray-800 disabled:opacity-50"
                            >
                              {t('deals.dealDetail.assignments.addAsCollaborator')}
                            </button>
                            {!assignments.find(a => a.assignment_type === 'primary') && (
                              <button
                                onClick={() => handleAssignUser(member.user_id, 'primary')}
                                disabled={assignmentLoading}
                                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                              >
                                {t('deals.dealDetail.assignments.addAsPrimary')}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    
                    {teamMembers.filter(member => !assignments.find(a => a.user_id === member.user_id)).length === 0 && (
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        {t('deals.dealDetail.assignments.allTeamMembersAssigned')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Completion Modal */}
      {completionModal.isOpen && completionModal.task && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 w-full max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-y-auto widget-scrollbar shadow-2xl">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                {t('tasks.modals.taskCompleted')}
              </h3>
              <button
                onClick={() => {
                  setCompletionModal({ isOpen: false, task: null })
                  setFollowUpDescription('')
                  setFollowUpPriority('medium')
                  setFollowUpSelectedDate('')
                  setFollowUpCustomDate('')
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-2 touch-manipulation"
              >
                ✕
              </button>
            </div>

            {/* Completed Task Info */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
                  ✓
                </div>
                <div>
                  <div className="font-medium text-green-800 dark:text-green-200">
                    {completionModal.task.description}
                  </div>
                  <div className="text-sm text-green-600 dark:text-green-300">
                    {completionModal.task.customer_name}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Follow-up Description */}
              <div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('tasks.modals.scheduleFollowUp')}
                </div>
                <textarea
                  value={followUpDescription}
                  onChange={(e) => setFollowUpDescription(e.target.value)}
                  placeholder={t('tasks.modals.followUpPlaceholder')}
                  className="w-full px-4 py-3 text-lg border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:border-green-500 focus:outline-none transition-colors"
                  rows={2}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.metaKey && followUpDescription.trim()) {
                      e.preventDefault()
                      scheduleFollowUp()
                    }
                  }}
                />
              </div>

              {/* Follow-up Priority Selection */}
              {followUpDescription.trim() && (
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('tasks.modals.priorityLevel')}
                  </div>
                  <div className="flex space-x-2">
                    {(['low', 'medium', 'high'] as const).map((priority) => (
                      <button
                        key={priority}
                        type="button"
                        onClick={() => setFollowUpPriority(priority)}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          followUpPriority === priority
                            ? priority === 'high' 
                              ? 'bg-red-100 text-red-800 border-2 border-red-300' 
                              : priority === 'medium'
                              ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-300'
                              : 'bg-green-100 text-green-800 border-2 border-green-300'
                            : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:border-gray-300'
                        }`}
                      >
                        {translatePriority(priority)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick Date Buttons */}
              {followUpDescription.trim() && (
                <div>
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('tasks.modals.whenShouldBeDone')}
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {getNext7Days().map((day) => (
                      <button
                        key={day.date}
                        onClick={() => {
                          setFollowUpSelectedDate(day.date)
                          setFollowUpCustomDate('')
                        }}
                        className={`p-3 border-2 rounded-lg text-center transition-all ${
                          followUpSelectedDate === day.date
                            ? 'border-gray-900 bg-blue-50 dark:bg-blue-900/20'
                            : day.isToday
                            ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20 hover:border-orange-400'
                            : day.isTomorrow
                            ? 'border-green-300 bg-green-50 dark:bg-green-900/20 hover:border-green-400'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <div className={`text-xs font-medium ${
                          followUpSelectedDate === day.date ? 'text-blue-700 dark:text-blue-300' :
                          day.isToday ? 'text-orange-700 dark:text-orange-300' :
                          day.isTomorrow ? 'text-green-700 dark:text-green-300' :
                          'text-gray-600 dark:text-gray-400'
                        }`}>
                          {day.label}
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Custom Date Input */}
                  <div className="flex items-center space-x-3 mt-4">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.modals.orPickDate')}</span>
                    <input
                      type="date"
                      value={followUpCustomDate}
                      onChange={(e) => {
                        setFollowUpCustomDate(e.target.value)
                        setFollowUpSelectedDate('')
                      }}
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-gray-900 focus:outline-none"
                    />
                  </div>

                  <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-3">
                    ⌘ + Enter for Tomorrow
                  </div>
                </div>
              )}

              {/* Context Info */}
              {followUpDescription.trim() && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <div className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>{t('tasks.modals.customer')}:</strong> {completionModal.task.customer_name}
                    {completionModal.task.deal_id && (
                      <div className="text-xs text-gray-900 dark:text-blue-300 mt-1">
                        {t('tasks.modals.willBeLinkedToDeal')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t('tasks.modals.skipFollowUp')}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={confirmTaskCompletion}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
                >
                  {t('tasks.modals.justComplete')}
                </button>
                {followUpDescription.trim() && (
                  <button
                    onClick={scheduleFollowUp}
                    className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  >
                    {t('tasks.modals.completeAndFollowUp')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateTaskModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-2xl shadow-2xl max-h-[95vh] overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2">{t('tasks.modals.quickTask')}</h3>
                  <p className="text-blue-100 text-sm">{t('tasks.modals.createTaskForDeal', { defaultValue: 'Create a new task for this deal' })}</p>
                </div>
                <button
                  onClick={() => setShowCreateTaskModal(false)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-8 overflow-y-auto widget-scrollbar max-h-[calc(95vh-120px)]">
            <form onSubmit={async (e) => {
              e.preventDefault()
              const formData = new FormData(e.currentTarget)
              const description = formData.get('description') as string
              const selectedDate = formData.get('selected_date') as string
              const customDate = formData.get('custom_date') as string
              const priority = formData.get('priority') as string || 'medium'
              
              try {
                const token = localStorage.getItem('token')
                if (!token) throw new Error('Authentication required')

                // Use selected date or custom date, default to tomorrow
                let taskDueDate = selectedDate || customDate
                if (!taskDueDate) {
                  const tomorrow = new Date()
                  tomorrow.setDate(tomorrow.getDate() + 1)
                  taskDueDate = tomorrow.toISOString().split('T')[0]
                }

                const taskData = {
                  customer_id: deal?.deal_customers?.[0]?.customer_id || deal?.customer_id,
                  deal_id: deal?.id,
                  description,
                  due_date: taskDueDate,
                  priority,
                  status: 'pending'
                }
                
                const response = await makeApiCall('api/tasks', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify(taskData)
                })

                if (!response.ok) throw new Error('Failed to create task')

                setShowCreateTaskModal(false)
                // Refresh tasks
                fetchDealTasks()
              } catch (err) {
                console.error('Error creating task:', err)
                alert(t('deals.dealDetail.taskCreationError', { defaultValue: 'Failed to create task. Please try again.' }))
              }
            }}>
              <div className="space-y-6">
                {/* Deal Context */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-semibold text-blue-900 dark:text-blue-100">{deal?.name}</div>
                        <div className="text-sm text-blue-700 dark:text-blue-300">
                          {deal?.deal_customers?.[0]?.name || deal?.customer_name}
                        </div>
                      </div>
                    </div>
                    <div className="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full">
                      {t('common.deal', { defaultValue: 'DEAL' })}
                    </div>
                  </div>
                </div>

                {/* Task Description */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    {t('tasks.modals.whatNeedsToBeDone')} *
                  </label>
                  <textarea
                    name="description"
                    placeholder={t('tasks.modals.taskDescriptionPlaceholder', { defaultValue: 'e.g., Follow up on mortgage application' })}
                    className="w-full px-4 py-3 text-lg border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
                    rows={3}
                    required
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.metaKey) {
                        e.preventDefault()
                        e.currentTarget.form?.requestSubmit()
                      }
                    }}
                  />
                </div>

                {/* Priority Selection */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    {t('tasks.modals.priority')}
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {(['low', 'medium', 'high'] as const).map((priority) => (
                      <label key={priority} className="flex-1 min-w-[100px]">
                        <input 
                          type="radio" 
                          name="priority" 
                          value={priority} 
                          defaultChecked={priority === 'medium'} 
                          className="peer sr-only" 
                        />
                        <div className={`cursor-pointer text-center px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
                          priority === 'high'
                            ? 'peer-checked:bg-gradient-to-r peer-checked:from-red-500 peer-checked:to-red-600 peer-checked:text-white peer-checked:shadow-lg peer-checked:scale-105 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600'
                            : priority === 'medium'
                            ? 'peer-checked:bg-gradient-to-r peer-checked:from-yellow-500 peer-checked:to-yellow-600 peer-checked:text-white peer-checked:shadow-lg peer-checked:scale-105 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600'
                            : 'peer-checked:bg-gradient-to-r peer-checked:from-green-500 peer-checked:to-green-600 peer-checked:text-white peer-checked:shadow-lg peer-checked:scale-105 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-gray-200 dark:border-gray-600'
                        }`}>
                          {priority === 'low' ? t('tasks.priorities.low') : priority === 'medium' ? t('tasks.priorities.medium') : t('tasks.priorities.high')}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Quick Date Selection */}
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                    {t('tasks.modals.whenIsThisDue')}
                  </label>
                  
                  {/* Quick Date Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
                    {getNext7Days().map((day) => (
                      <label key={day.date} className="cursor-pointer">
                        <input 
                          type="radio" 
                          name="selected_date" 
                          value={day.date}
                          defaultChecked={day.isTomorrow}
                          className="peer sr-only"
                        />
                        <div className={`text-center p-3 border-2 rounded-xl transition-all ${
                          day.isToday
                            ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 peer-checked:border-orange-500 peer-checked:shadow-md'
                            : day.isTomorrow
                            ? 'border-green-300 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 peer-checked:border-orange-500 peer-checked:shadow-md peer-checked:bg-orange-50 dark:peer-checked:bg-orange-900/20 peer-checked:text-orange-700 dark:peer-checked:text-orange-300'
                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 peer-checked:border-orange-500 peer-checked:bg-orange-50 dark:peer-checked:bg-orange-900/20 peer-checked:text-orange-700 dark:peer-checked:text-orange-300 peer-checked:shadow-md'
                        } hover:border-gray-300`}>
                          <div className="text-xs font-medium">
                            {day.label}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>

                  {/* Custom Date Input */}
                  <div className="flex items-center space-x-3">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.modals.orPickDate')}</span>
                    <input
                      type="date"
                      name="custom_date"
                      className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Form Buttons */}
                <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowCreateTaskModal(false)}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 font-semibold text-lg"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                  >
                    {t('deals.dealDetail.createTask')}
                  </button>
                </div>

                <div className="text-xs text-center text-gray-500 dark:text-gray-400 mt-2">
                  {t('tasks.modals.cmdEnterHint')}
                </div>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}

      {/* Partner Creation Modal */}
      {showPartnerCreationModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {t('partners.addPartner')}
                </h2>
                <button
                  onClick={() => setShowPartnerCreationModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XIcon className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('partners.form.partnerInformation')}
              </p>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault()
                const formData = new FormData(e.currentTarget)
                const firstName = formData.get('first_name') as string
                const lastName = formData.get('last_name') as string
                const email = formData.get('email') as string
                const phone = formData.get('phone') as string
                const agency = formData.get('agency') as string
                const commissionCut = formData.get('commission_cut') as string

                const newPartner = await createPartner(firstName, lastName, email, phone, agency, commissionCut)
                if (newPartner) {
                  // Auto-populate commission cut if partner has one set
                  setNewSplitData({
                    partner: newPartner,
                    split_type: newPartner.commission_cut ? 'percentage' : newSplitData.split_type,
                    split_value: newPartner.commission_cut ? newPartner.commission_cut.toString() : newSplitData.split_value,
                    notes: newSplitData.notes
                  })
                  setPartnerSearchQuery(newPartner.name || `${newPartner.first_name} ${newPartner.last_name}`)
                  setShowPartnerCreationModal(false)
                }
              }}
              className="p-6"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.firstName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder={t('partners.form.enterFirstName')}
                      defaultValue={partnerSearchQuery.split(' ')[0] || ''}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('customers.lastName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder={t('partners.form.enterLastName')}
                      defaultValue={partnerSearchQuery.split(' ').slice(1).join(' ') || partnerSearchQuery.split(' ')[0] || ''}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('common.email')} <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder={t('partners.form.enterEmail')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('common.phone')} <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder={t('partners.form.enterPhone')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('partners.form.agency')} <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      name="agency"
                      placeholder={t('partners.form.enterAgency')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('partners.form.commissionCut')} <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="number"
                      name="commission_cut"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder={t('partners.form.enterCommissionCut')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-600"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Percentage of commission split</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowPartnerCreationModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-lg hover:bg-gray-800 dark:hover:bg-gray-600"
                >
                  {t('common.create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </FullWidthContent>
  )
}

export default DealDetail